/* SWITZ — global filtering engine for every product listing (collections and
 * search). Loaded by sections/switz-snapback-grid.liquid.
 *
 * Shopify stays the source of truth: every change builds Shopify's own filter
 * URL (filter.p.tag, filter.v.availability, filter.v.price.gte/lte, sort_by,
 * page), puts it in the address bar with history.pushState, and fetches ONLY
 * this section through the Section Rendering API (?section_id=...). The page
 * never reloads and scroll position is never touched, except that changing
 * page in pagination brings the top of the grid into view.
 *
 * Only elements marked data-switz-region are swapped (grid, count, chips,
 * colour and availability lists). The price slider is synced from the response
 * instead of being replaced, so it never jumps under the customer's finger.
 *
 * Races: each request aborts the previous one (AbortController) and carries a
 * sequence number, so an older response can never overwrite a newer choice.
 *
 * Product cards need no re-initialising: the carousel, wishlist and drawer use
 * delegated listeners, and <product-form> is a custom element that upgrades
 * itself when inserted.
 */
(function () {
  'use strict';

  var MANAGED = /^(filter\.|sort_by$|page$)/;
  var STEPS = [5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2500, 5000, 10000];

  function Listing(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.seq = 0;
    this.ctrl = null;
    this.timer = 0;
    this.bind();
    this.initPrice(false);
  }

  Listing.prototype.$ = function (sel) { return this.root.querySelector(sel); };
  Listing.prototype.$$ = function (sel) { return Array.prototype.slice.call(this.root.querySelectorAll(sel)); };

  /* ------------------------------------------------------------------ */
  /* URL building                                                        */
  /* ------------------------------------------------------------------ */

  Listing.prototype.priceState = function () {
    var box = this.$('[data-switz-price]');
    if (!box) return null;
    var max = parseFloat(box.getAttribute('data-max')) || 0;
    var fMin = box.querySelector('[data-switz-price-min]');
    var fMax = box.querySelector('[data-switz-price-max]');
    var lo = fMin.value === '' ? null : parseFloat(fMin.value);
    var hi = fMax.value === '' ? null : parseFloat(fMax.value);
    if (lo !== null && (isNaN(lo) || lo <= 0)) lo = null;
    if (hi !== null && (isNaN(hi) || hi >= max)) hi = null;
    if (lo !== null && hi !== null && lo > hi) { var t = lo; lo = hi; hi = t; }
    return {
      box: box, max: max, lo: lo, hi: hi,
      pMin: box.getAttribute('data-param-min'), pMax: box.getAttribute('data-param-max')
    };
  };

  Listing.prototype.buildUrl = function () {
    var current = new URL(window.location.href);
    var p = new URLSearchParams();
    current.searchParams.forEach(function (v, k) { if (!MANAGED.test(k)) p.append(k, v); });

    var form = this.$('[data-switz-filters]');
    if (form) {
      Array.prototype.forEach.call(form.querySelectorAll('input[type="checkbox"][name]'), function (i) {
        if (i.checked && !i.disabled) p.append(i.name, i.value);
      });
    }

    var ps = this.priceState();
    if (ps) {
      if (ps.lo !== null) p.append(ps.pMin, String(ps.lo));
      if (ps.hi !== null) p.append(ps.pMax, String(ps.hi));
    }

    var sort = this.$('[data-switz-sort-select]');
    if (sort && sort.value && sort.value !== sort.getAttribute('data-default')) p.append('sort_by', sort.value);

    var qs = p.toString();
    return current.pathname + (qs ? '?' + qs : '');
  };

  /* ------------------------------------------------------------------ */
  /* Fetching and swapping                                               */
  /* ------------------------------------------------------------------ */

  Listing.prototype.schedule = function (ms) {
    var self = this;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(function () { self.go(self.buildUrl(), { push: true }); }, ms || 0);
  };

  Listing.prototype.go = function (url, opts) {
    var self = this;
    opts = opts || {};
    window.clearTimeout(this.timer);

    var target = new URL(url, window.location.href);
    target.searchParams.delete('section_id');
    var clean = target.pathname + target.search;

    if (opts.push && clean !== window.location.pathname + window.location.search) {
      window.history.pushState({ switz: true }, '', clean);
    }

    if (this.ctrl) this.ctrl.abort();
    var ctrl = this.ctrl = (window.AbortController ? new AbortController() : null);
    var mine = ++this.seq;
    this.root.classList.add('is-loading');
    this.root.setAttribute('aria-busy', 'true');

    var fetchUrl = new URL(clean, window.location.href);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    fetch(fetchUrl.toString(), { signal: ctrl ? ctrl.signal : undefined, headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (mine !== self.seq) return; // a newer request has started
        self.render(html, opts);
        if (opts.after) opts.after();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (mine !== self.seq) return;
        window.location.href = clean; // last resort: plain navigation still works
      })
      .then(function () {
        if (mine !== self.seq) return;
        self.root.classList.remove('is-loading');
        self.root.removeAttribute('aria-busy');
      });
  };

  Listing.prototype.render = function (html, opts) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.querySelector('[data-switz-listing]');
    if (!next) throw new Error('listing missing');

    this.$$('[data-switz-region]').forEach(function (el) {
      var key = el.getAttribute('data-switz-region');
      var fresh = next.querySelector('[data-switz-region="' + key + '"]');
      if (!fresh || fresh.innerHTML === el.innerHTML) return;

      // keep keyboard focus on the same control after the swap
      var focusKey = null;
      var a = document.activeElement;
      if (a && el.contains(a)) {
        if (a.hasAttribute('data-switz-all')) focusKey = '[data-switz-all]';
        else if (a.name) focusKey = 'input[name="' + a.name + '"][value="' + (a.value || '').replace(/"/g, '\\"') + '"]';
      }
      el.innerHTML = fresh.innerHTML;
      if (focusKey) {
        var back = el.querySelector(focusKey);
        if (back) back.focus({ preventScroll: true });
      }
    });

    // Sort select reflects the URL (Back/Forward, chips).
    var sort = this.$('[data-switz-sort-select]');
    var freshSort = next.querySelector('[data-switz-sort-select]');
    if (sort && freshSort && sort.value !== freshSort.value) sort.value = freshSort.value;

    var box = this.$('[data-switz-price]');
    var freshBox = next.querySelector('[data-switz-price]');
    if (box && freshBox) {
      ['data-max', 'data-max-label', 'data-param-min', 'data-param-max', 'data-min-value', 'data-max-value'].forEach(function (k) {
        box.setAttribute(k, freshBox.getAttribute(k) || '');
      });
      this.initPrice(!!opts.syncAll);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Price: slider + fields + quick ranges                               */
  /* ------------------------------------------------------------------ */

  function money(sym, n) { return sym + (Math.round(n * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 }); }

  Listing.prototype.initPrice = function (syncValues) {
    var box = this.$('[data-switz-price]');
    if (!box) return;
    var max = parseFloat(box.getAttribute('data-max')) || 0;
    var sym = box.getAttribute('data-symbol') || '';
    var fMin = box.querySelector('[data-switz-price-min]');
    var fMax = box.querySelector('[data-switz-price-max]');
    var rMin = box.querySelector('[data-switz-range-min]');
    var rMax = box.querySelector('[data-switz-range-max]');

    Array.prototype.forEach.call(box.querySelectorAll('[data-switz-price-top]'), function (s) {
      s.textContent = box.getAttribute('data-max-label');
    });
    [rMin, rMax, fMin, fMax].forEach(function (i) { i.max = String(max); });

    if (syncValues) {
      fMin.value = box.getAttribute('data-min-value') || '';
      fMax.value = box.getAttribute('data-max-value') || '';
    }
    this.syncSliderFromFields();

    var step = STEPS[STEPS.length - 1];
    for (var i = 0; i < STEPS.length; i++) {
      if (Math.ceil(max / STEPS[i]) <= 5) { step = STEPS[i]; break; }
    }
    var wrap = box.querySelector('[data-switz-ranges]');
    var html = '<button type="button" class="switz-qr" data-qr-min="" data-qr-max="">All prices</button>';
    if (max > 0) {
      var n = Math.ceil(max / step);
      for (var k = 0; k < n; k++) {
        var lo = k * step, hi = (k + 1) * step;
        var label = k === 0 ? 'Under ' + money(sym, hi) : money(sym, lo) + ' – ' + money(sym, hi);
        html += '<button type="button" class="switz-qr" data-qr-min="' + (k === 0 ? '' : lo) + '" data-qr-max="' + hi + '">' + label + '</button>';
      }
    }
    if (wrap.getAttribute('data-built') !== html) {
      wrap.innerHTML = html;
      wrap.setAttribute('data-built', html);
    }
    this.markQuick();
  };

  Listing.prototype.syncSliderFromFields = function () {
    var ps = this.priceState();
    if (!ps) return;
    var rMin = ps.box.querySelector('[data-switz-range-min]');
    var rMax = ps.box.querySelector('[data-switz-range-max]');
    rMin.value = String(ps.lo === null ? 0 : Math.floor(ps.lo));
    rMax.value = String(ps.hi === null ? ps.max : Math.ceil(ps.hi));
    this.paintFill();
  };

  Listing.prototype.paintFill = function () {
    var box = this.$('[data-switz-price]');
    if (!box) return;
    var max = parseFloat(box.getAttribute('data-max')) || 0;
    var sym = box.getAttribute('data-symbol') || '';
    var rMin = box.querySelector('[data-switz-range-min]');
    var rMax = box.querySelector('[data-switz-range-max]');
    var range = box.querySelector('.switz-range');
    var lo = max ? (parseFloat(rMin.value) / max) * 100 : 0;
    var hi = max ? (parseFloat(rMax.value) / max) * 100 : 100;
    range.style.setProperty('--lo', lo + '%');
    range.style.setProperty('--hi', hi + '%');
    rMin.setAttribute('aria-valuetext', sym + rMin.value);
    rMax.setAttribute('aria-valuetext', sym + rMax.value);
    // keep the lower thumb reachable when both sit at the top end
    rMin.style.zIndex = parseFloat(rMin.value) >= max - 0.5 ? '3' : '';
  };

  Listing.prototype.markQuick = function () {
    var ps = this.priceState();
    if (!ps) return;
    var lo = ps.lo === null ? 0 : ps.lo;
    var hi = ps.hi === null ? ps.max : ps.hi;
    Array.prototype.forEach.call(ps.box.querySelectorAll('.switz-qr'), function (b) {
      var bl = b.getAttribute('data-qr-min') === '' ? 0 : parseFloat(b.getAttribute('data-qr-min'));
      var bh = b.getAttribute('data-qr-max') === '' ? ps.max : Math.min(parseFloat(b.getAttribute('data-qr-max')), ps.max);
      b.setAttribute('aria-pressed', bl === lo && bh === hi ? 'true' : 'false');
    });
  };

  /* ------------------------------------------------------------------ */
  /* Events (delegated on the listing, so swapped markup keeps working)  */
  /* ------------------------------------------------------------------ */

  Listing.prototype.bind = function () {
    var self = this;
    var root = this.root;

    root.addEventListener('change', function (e) {
      var t = e.target;

      if (t.matches('[data-switz-all]')) {
        var list = t.closest('[data-switz-region]');
        Array.prototype.forEach.call(list.querySelectorAll('[data-switz-colour]'), function (c) {
          c.checked = false;
          c.closest('.switz-colour').classList.remove('is-active');
        });
        t.checked = true;
        t.closest('.switz-colour').classList.add('is-active');
        self.schedule(0);
        return;
      }

      if (t.matches('[data-switz-colour]')) {
        var ul = t.closest('[data-switz-region]');
        var any = ul.querySelectorAll('[data-switz-colour]:checked').length > 0;
        var all = ul.querySelector('[data-switz-all]');
        if (all) {
          all.checked = !any;
          all.closest('.switz-colour').classList.toggle('is-active', !any);
        }
        t.closest('.switz-colour').classList.toggle('is-active', t.checked);
        self.schedule(0);
        return;
      }

      if (t.matches('.switz-check[name]')) {
        t.closest('.switz-colour').classList.toggle('is-active', t.checked);
        self.schedule(0);
        return;
      }

      if (t.matches('[data-switz-sort-select]')) { self.schedule(0); return; }

      // slider released (or keyboard step finished): one request for the final value
      if (t.matches('[data-switz-range-min], [data-switz-range-max]')) { self.schedule(250); return; }

      // typed price confirmed with Enter / blur
      if (t.matches('[data-switz-price-min], [data-switz-price-max]')) { self.schedule(0); }
    });

    root.addEventListener('input', function (e) {
      var t = e.target;
      if (t.matches('[data-switz-range-min], [data-switz-range-max]')) {
        var box = t.closest('[data-switz-price]');
        var rMin = box.querySelector('[data-switz-range-min]');
        var rMax = box.querySelector('[data-switz-range-max]');
        var max = parseFloat(box.getAttribute('data-max')) || 0;
        if (parseFloat(rMin.value) > parseFloat(rMax.value)) {
          if (t === rMin) rMin.value = rMax.value; else rMax.value = rMin.value;
        }
        var lo = parseFloat(rMin.value), hi = parseFloat(rMax.value);
        box.querySelector('[data-switz-price-min]').value = lo > 0 ? String(lo) : '';
        box.querySelector('[data-switz-price-max]').value = hi < max ? String(hi) : '';
        self.paintFill();
        self.markQuick();
        return;
      }
      if (t.matches('[data-switz-price-min], [data-switz-price-max]')) {
        self.syncSliderFromFields();
        self.markQuick();
        self.schedule(450); // wait for typing to stop
      }
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('[data-switz-price-min], [data-switz-price-max]')) {
        e.preventDefault();
        self.schedule(0);
      }
    });

    root.addEventListener('submit', function (e) {
      if (e.target.matches('[data-switz-filters], [data-switz-sort]')) {
        e.preventDefault();
        self.schedule(0);
      }
    });

    root.addEventListener('click', function (e) {
      var qr = e.target.closest('.switz-qr');
      if (qr && root.contains(qr)) {
        e.preventDefault();
        var box = qr.closest('[data-switz-price]');
        box.querySelector('[data-switz-price-min]').value = qr.getAttribute('data-qr-min');
        var mx = qr.getAttribute('data-qr-max');
        var max = parseFloat(box.getAttribute('data-max')) || 0;
        box.querySelector('[data-switz-price-max]').value = mx !== '' && parseFloat(mx) < max ? mx : '';
        self.syncSliderFromFields();
        self.markQuick();
        self.schedule(0);
        return;
      }

      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest('a[data-switz-link], [data-switz-pages] a[href]');
      if (!link || !root.contains(link)) return;
      var url = new URL(link.getAttribute('href'), window.location.href);
      if (url.origin !== window.location.origin) return;
      e.preventDefault();
      var isPage = !!link.closest('[data-switz-pages]');
      self.go(url.pathname + url.search, {
        push: true,
        syncAll: true,
        after: isPage ? function () {
          var grid = self.$('#ProductGridContainer');
          var top = grid ? grid.getBoundingClientRect().top : 0;
          if (top < 0) window.scrollBy({ top: top - 90, behavior: 'smooth' });
        } : null
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Start                                                               */
  /* ------------------------------------------------------------------ */

  var listings = [];
  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-switz-listing]'), function (root) {
      if (root.__switz) return;
      root.__switz = new Listing(root);
      listings.push(root.__switz);
    });
  }

  // Back / Forward: re-render from the URL, Shopify's filters decide the state.
  window.addEventListener('popstate', function () {
    listings.forEach(function (l) {
      if (document.body.contains(l.root)) l.go(window.location.href, { push: false, syncAll: true });
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Theme editor re-renders the section in place.
  document.addEventListener('shopify:section:load', start);
})();
